import React, { useState, useEffect } from 'react'
import AuthLayout from '../../../../layout/AuthLayout'
import { Box, Flex, Text, Group, TextInput, Button, Pagination, Modal, ScrollArea, Drawer, Badge } from '@mantine/core'
import classes from '../../../../assets/module-css/AdminLayout.module.css'
import { useDispatch } from 'react-redux'
import { updateRoute } from '../../../../store/dataRoute'
import { getAllCountryList } from '../../../../services/location'
import { useDebouncedCallback } from '@mantine/hooks'
import LoadingData from '../../../../component/ui/LoadingData'
import NoData from '../../../../component/ui/NoData'
import TableCountry from '../../../../component/pages/Country/TableCountry'
import FormFilterCountry from '../../../../component/pages/Country/FormFilterCountry'
import FormCountry from '../../../../component/pages/Country/FormCountry'
import ModalDefaultPayment from './ModalDefaultPayment'

const defaultParameter = {
    'page[num]': 1,
    'page[limit]': 10,
    sort: 'name',
}

const labelTable = [
    { label: 'ID', width: 50 },
    { label: 'Name', width: 200 },
    { label: 'Currency', width: 100 },
    { label: 'ISO Code', width: 120 },
    { label: 'Postcode Req', width: 120 },
    { label: 'Status', width: 100 },
    { label: 'Units (Weight/Dim)', width: 150 },
    { label: 'Action', width: 50 },
]

const CountryList = () => {
    const dispatch = useDispatch()
    const [params, setParams] = useState(defaultParameter)
    const [countryList, setCountryList] = useState([])
    const [totalData, setTotalData] = useState(0)
    const [loading, setLoading] = useState(true)
    const [openModalFilter, setOpenModalFilter] = useState(false)
    const [openModalForm, setOpenModalForm] = useState(false)
    const [openModalDefaultPayment, setOpenModalDefaultPayment] = useState(false)
    const [detailData, setDetailData] = useState(null)

    const mappingRoute = [
        { label: '/', route: null },
        { label: 'Settings', route: null }, // Adjusted from User request "common & global settings", assuming this is the breadcrumb
        { label: 'Country List', route: null },
    ]

    const handleGetAllCountryList = useDebouncedCallback(async () => {
        setLoading(true)
        try {
            const response = await getAllCountryList(params)
            if (response.success) {
                const dataCountry = response.data
                // const countryIncluded = response.included // Not using included data for now based on requirement
                const countData = response.meta.pagination.total

                const remapCountry = dataCountry.map((val) => {
                    return {
                        id: val.attributes.id,
                        name: val.attributes.name,
                        currency: val.attributes.currency,
                        iso_code_2: val.attributes.iso_code_2,
                        iso_code_3: val.attributes.iso_code_3,
                        postcode_required: val.attributes.postcode_required,
                        status: val.attributes.status,
                        weight_unit: val.attributes.weight_unit,
                        dimension_unit: val.attributes.dimension_unit,
                    }
                })

                setCountryList(remapCountry)
                setTotalData(countData)
            } else {
                setCountryList([])
                setTotalData(0)
            }
        } catch (error) {
            console.log(error)
            setCountryList([])
        } finally {
            setLoading(false)
        }
    }, 500)

    useEffect(() => {
        dispatch(updateRoute({ 'data': mappingRoute }))
        // eslint-disable-next-line 
    }, [dispatch])

    useEffect(() => {
        handleGetAllCountryList()
        // eslint-disable-next-line
    }, [params])

    const actions = {
        edit: (val) => {
            setDetailData(val)
            setOpenModalForm(true)
        },
        defaultPayment: (val) => {
            setDetailData(val)
            setOpenModalDefaultPayment(true)
        }
    }

    const handleAction = (val, type) => {
        return actions[type](val)
    }

    const handleChangeFilter = (name, val) => {
        setParams((oldVal) => ({ ...oldVal, [name]: val }))
    }

    const handleApplyFilter = (params) => {
        setParams((oldVal) => ({ ...oldVal, ...params }))
    }

    const handleChangePage = (val) => {
        // eslint-disable-next-line
        setParams((oldVal) => ({ ...oldVal, ['page[num]']: val }))
    }

    const onCloseFormFilter = () => {
        setOpenModalFilter(false)
    }

    const onCloseModalForm = () => {
        setOpenModalForm(false)
        setDetailData(null)
    }

    const onCloseModalDefaultPayment = () => {
        setOpenModalDefaultPayment(false)
        setDetailData(null)
    }

    const mappingFilterInfo = (parameter) => {
        if (parameter['filter[status][is]']) {
            return (
                <Box>
                    <Group gap='sm' align='center' mb={12}>
                        <Badge variant="light" color="black" tt='capitalize' fw='500'>
                            Status: {parameter['filter[status][is]'] === '1' ? 'Active' : 'Inactive'}
                        </Badge>
                    </Group>
                    <Group align='center' gap='sm'>
                        <Button size='compact-sm' fz={11} fw='500' variant="outline" onClick={() => setOpenModalFilter(true)}>Modify Filter</Button>
                        <Button size='compact-sm' fz={11} fw='500' variant="outline" onClick={() => setParams(defaultParameter)}>Clear Filter</Button>
                    </Group>
                </Box>
            )
        }
    }

    return (
        <AuthLayout>
            <Box my={20}>
                <Box>
                    <Flex justify='space-between' align="center" mb={30}>
                        <Text className={classes.titleLayout}>Country List</Text>
                        <Group gap='sm' align='center'>
                            <TextInput
                                value={params['filter[name][like]'] || ''}
                                placeholder='Search by name'
                                onChange={(val) => {
                                    handleChangeFilter('filter[name][like]', val.target.value)
                                    handleChangePage(1)
                                }}
                                size='xs'
                            />
                            <Button size='xs' variant="outline" onClick={() => setOpenModalFilter(true)}>Filter</Button>
                        </Group>
                    </Flex>
                </Box>
                {mappingFilterInfo(params)}
                <Box my={30}>
                    <Group mb={10} gap='xs'>
                        <Text fz={12}>Total Data Found: </Text>
                        <Text fz={12} fw='bold'>{totalData} Countries </Text>
                    </Group>
                    {loading ? <LoadingData /> : countryList.length > 0 ? <TableCountry label={labelTable} data={countryList} actionMethod={handleAction} /> : <NoData />}
                </Box>
                <Flex justify='end'>
                    <Pagination size='sm' onChange={handleChangePage} value={params['page[num]']} total={totalData > 0 ? Math.ceil(totalData / params['page[limit]']) : 0} />
                </Flex>
            </Box>
            <Drawer position='right' title='Filter Country' opened={openModalFilter} onClose={onCloseFormFilter} overlayProps={{ backgroundOpacity: 0.5, blur: 4 }}>
                <FormFilterCountry paramsFilter={params} applyFilter={handleApplyFilter} onClose={onCloseFormFilter} />
            </Drawer>
            <Modal opened={openModalForm} onClose={onCloseModalForm} centered closeOnClickOutside={false} size='xl' title={<Text fw='Bold'>Edit Country</Text>} scrollAreaComponent={ScrollArea.Autosize} withCloseButton={false}>
                <FormCountry dataDetail={detailData} onCloseModal={onCloseModalForm} reloadList={handleGetAllCountryList} />
            </Modal>
            <ModalDefaultPayment
                opened={openModalDefaultPayment}
                onClose={onCloseModalDefaultPayment}
                countryData={detailData}
                onSuccess={handleGetAllCountryList}
            />
        </AuthLayout>
    )
}

export default CountryList
